import { AppShell, Content } from '../../components/ui/AppShell';
import { Avatar } from '../../components/ui/bits';
import { Clock, Swords } from '../../components/ui/icons';
import type { DuelDetail, DuelSide } from '../../lib/api';
import { countdownTo } from '../../lib/format';
import { BackBar } from './BackBar';

/** Screen 2 — challenge sent, waiting for the opponent (challenger's view). */
export function ChallengeSent({
  data,
  rival,
  roundOpen,
  iHaveEntry,
  onBack,
  onMakePicks,
  onCancel,
  cancelling
}: {
  data: DuelDetail;
  rival: DuelSide;
  roundOpen: boolean;
  iHaveEntry: boolean;
  onBack: () => void;
  onMakePicks: () => void;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const round = data.round;
  const isCycling = data.sport === 'CYCLING';
  const fixtureCount = data.fixtures.length;
  const rivalFirst = rival.displayName.split(/\s+/)[0];

  return (
    <AppShell>
      <Content>
        <BackBar onBack={onBack} label="Friends" eyebrow={`${round?.label ?? 'Round'} · Head-to-head duel`} />

        {/* hero confirmation */}
        <div className="relative mt-3 overflow-hidden rounded-[30px] bg-gradient-to-b from-[#1C1C24] to-[#0A0A0E] px-6 py-10 text-center shadow-[0_24px_46px_-18px_rgba(0,0,0,0.6)]">
          <span className="pointer-events-none absolute left-1/2 top-1/2 h-[260px] w-[260px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.22),transparent_70%)]" />
          <div className="relative flex flex-col items-center">
            <div className="grid h-[88px] w-[88px] place-items-center rounded-full bg-gradient-to-br from-[#24C56A] to-[#08A079] shadow-[0_20px_40px_-14px_rgba(10,150,110,0.7)]">
              <Swords width={38} height={38} strokeWidth={2} className="text-white" />
            </div>
            <div className="mt-6 text-[26px] font-extrabold tracking-tight text-white">Challenge sent</div>
            <div className="mt-[10px] max-w-[280px] text-[14px] font-medium leading-[1.5] text-[#9A9AA4]">
              {rival.displayName} has been notified. The duel begins as soon as they accept.
            </div>
            <div className="mt-6 inline-flex items-center gap-[9px] rounded-full border border-white/14 bg-white/[0.08] py-[9px] pl-[13px] pr-[16px]">
              <Avatar name={rival.displayName} tone={rival.avatarTone} size={30} />
              <span className="text-[13px] font-bold text-white">Waiting for {rivalFirst}</span>
              <span className="flex gap-[3px]">
                <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
                <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent [animation-delay:0.2s]" />
                <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent [animation-delay:0.4s]" />
              </span>
            </div>
          </div>
        </div>

        {/* summary */}
        <div className="mt-4 rounded-2xl border border-black/5 bg-card p-4 shadow-tile">
          <div className="flex items-center gap-[11px]">
            <div className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-[13px] bg-[#5B6EE1]/12">
              <Swords width={22} height={22} strokeWidth={2} className="text-[#4453B8]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-extrabold tracking-snug text-ink">
                {round?.label ?? 'Round'} duel · vs {rivalFirst}
              </div>
              <div className="text-[12px] font-medium text-ink2">
                {fixtureCount > 0 ? `${fixtureCount} ${isCycling ? 'duels' : 'fixtures'}` : isCycling ? 'Duels' : 'Fixtures'}
                {round ? ` · locks in ${countdownTo(round.firstKickoffAt)}` : ''}
              </div>
            </div>
          </div>
          <div className="mt-[14px] flex items-start gap-[9px] border-t border-black/[0.06] pt-[14px]">
            <Clock width={15} height={15} strokeWidth={2} className="mt-[2px] shrink-0 text-ink2" />
            <span className="text-[12px] font-medium leading-[1.45] text-ink2">
              If {rivalFirst} doesn’t accept before {isCycling ? 'the stage locks' : 'cards lock'}, the challenge expires automatically.
            </span>
          </div>
        </div>

        <div className="mt-[14px] flex gap-[10px]">
          <button
            type="button"
            onClick={onMakePicks}
            disabled={!roundOpen}
            className="tap flex flex-1 items-center justify-center rounded-[15px] bg-night py-[14px] text-[14px] font-extrabold text-white disabled:opacity-40"
          >
            {iHaveEntry
              ? isCycling
                ? 'Edit my duels'
                : 'Edit my cards'
              : roundOpen
                ? isCycling
                  ? 'Call my duels now'
                  : 'Build my cards now'
                : isCycling
                  ? 'Stage opens soon'
                  : 'Cards open soon'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelling}
            className="tap flex items-center justify-center rounded-[15px] border border-black/8 bg-card px-[18px] py-[14px] text-[14px] font-bold text-ink2 disabled:opacity-50"
          >
            {cancelling ? '…' : 'Cancel'}
          </button>
        </div>
      </Content>
    </AppShell>
  );
}
